import { CSSProperties, FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input } from '../atoms';
import { useToast } from '../organisms';
import brandLogo from '../assets/brand/appealstack-logo.svg';
import { LegalFooterLinks } from './LegalPage';

/**
 * LoginPage — v2 auth surface at /login (FR-6 cutover: V2 owns '/').
 *
 * Composed pattern (no canonical M3 login page; M3 documents Text Field +
 * Filled Button on a centered Card surface). Per Agent Protocol step 2,
 * derives from M3 form patterns (text-fields + buttons + cards spec).
 *
 * Two sign-in paths, driven by the real, public /api/auth/methods flags:
 *   - In DEMO mode (methods.demoMode) every account has its own password, so the
 *     email + password form is the primary "Sign in" for staff and clients alike and
 *     renders first; the shared-secret box follows it, labelled "Shared staff password",
 *     for instances that still use one staff login. Outside demo mode the order is the
 *     historical one: the shared-secret box first, the portal path after it.
 *   - Staff / demo sign-in — the shared-secret password field. POSTs
 *     /api/auth/login with `{password}` (legacy shared secret) OR is also the
 *     surface a staff user uses.
 *   - Client portal sign-in — depends on deployment:
 *       • DEMO (methods.demoMode): a direct email + password form that POSTs
 *         /api/auth/login `{email,password}` (per-user bcrypt). WorkOS redirect
 *         URIs aren't wired for the demo host, so the SSO bounce is replaced by
 *         this real client login. On success we hard-reload to '/' so the app
 *         re-boots and routes the client (role=client) into the portal.
 *       • PROD (methods.workos && !demoMode): the AuthKit SSO bounce to
 *         /api/auth/workos/start (unchanged).
 *
 * Backend wire:
 *   - POST /api/auth/login → sets `appealstack_session` cookie. 401 on failure.
 *   - GET /api/auth/methods → { workos, demoMode, demoClients[] } (no secrets).
 *   - The server serves the SPA shell for any unauthenticated navigation
 *     (src/app.js session gate), so this page renders without a session.
 *     Old /v2/login links 301-redirect to /login for back-compat.
 */

// 🔴 THE PORTAL QUICK-PICK COMES FROM THE SERVER, and both halves of that matter.
//
// It used to be a hardcoded pair from `seed-demo.js` plus a hardcoded password:
//
//     { label: 'Bluebonnet Holdings', email: 'robert@bluebonnet.example' }
//     const DEMO_CLIENT_PASSWORD = 'Demo!Pass2026'
//
// Two defects. (1) On any deployment seeded with a different customer those accounts do not
// exist, so the page advertised sign-ins that FAIL — the first thing a firm sees on their own
// demo, naming another firm's clients. (2) The password was a plaintext credential committed
// to source and shipped in the bundle to every visitor of every deployment.
//
// Now `/api/auth/methods` returns the real `role='client'` users for the deployed org, and
// the quick-pick fills the EMAIL ONLY. Filling the password too would put the credential back
// in the bundle to save one paste.
type DemoClient = { label: string; email: string };

export function LoginPage() {
  // ── Staff / demo shared-secret path ──
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Client portal path (demo: email+password) ──
  const [clientEmail, setClientEmail] = useState('');
  const [clientPassword, setClientPassword] = useState('');
  const [clientSubmitting, setClientSubmitting] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);

  // Sign-in surfaces to offer, from the real public /api/auth/methods flags.
  const [workosEnabled, setWorkosEnabled] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [demoClients, setDemoClients] = useState<DemoClient[]>([]);
  // Whether the shared one-password staff login can succeed at all (ALLOW_LEGACY_LOGIN
  // + APP_PASSWORD on the server). Defaults to TRUE so an instance whose /api/auth/methods
  // predates the flag, or has not answered yet, keeps its historical sign-in box.
  const [sharedPassword, setSharedPassword] = useState(true);
  // In demo mode (every account has its own password) the shared box is a trap: ATA's
  // staff typed their personal passwords into it three times on Oct 2 2026 and got
  // "Invalid credentials" each time. Keep it one click away rather than on screen.
  const [showShared, setShowShared] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const next = params.get('next') || '/';

  useEffect(() => {
    let active = true;
    // Promise.resolve() so a non-promise fetch mock (unit tests) can't throw here.
    Promise.resolve(fetch('/api/auth/methods', { credentials: 'same-origin' }))
      .then((r) => (r && r.ok ? r.json() : { workos: false, demoMode: false, demoClients: [] }))
      .then((m) => {
        if (!active) return;
        setWorkosEnabled(!!(m && m.workos));
        setDemoMode(!!(m && m.demoMode));
        setDemoClients(Array.isArray(m?.demoClients) ? m.demoClients : []);
        setSharedPassword(!(m && m.sharedPassword === false));
      })
      .catch(() => { /* leave the alternate entries hidden on error */ });
    return () => { active = false; };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || 'Invalid password');
        toast({ variant: 'error', message: body.error || 'Invalid password' });
        return;
      }
      // Invalidate the TanStack Query cache for current-user so the
      // dashboard does not hit the stale 401 error cached with retry:false
      // and immediately redirect back to /login (the auth loop bug).
      await queryClient.invalidateQueries({ queryKey: ['current-user'] });
      toast({ variant: 'success', message: 'Signed in.' });
      navigate(next, { replace: true });
    } catch (err) {
      const msg = (err as Error).message || 'Network error';
      setError(msg);
      toast({ variant: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  }

  // Shared client login — used by the manual form AND the demo quick-pick.
  // Takes creds as args (not state) so the quick-pick can sign in in one click
  // without waiting for the async setState to flush.
  async function loginAsClient(email: string, password: string) {
    setClientError(null);
    setClientSubmitting(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setClientError(body.error || 'Invalid credentials');
        toast({ variant: 'error', message: body.error || 'Invalid credentials' });
        return;
      }
      toast({ variant: 'success', message: 'Signed in.' });
      // Hard reload so the SPA re-boots with the fresh session; the app's role-based
      // router then lands a client in the portal and staff on the dashboard.
      window.location.assign(next);
    } catch (err) {
      const msg = (err as Error).message || 'Network error';
      setClientError(msg);
      toast({ variant: 'error', message: msg });
    } finally {
      setClientSubmitting(false);
    }
  }

  function handleClientSubmit(e: FormEvent) {
    e.preventDefault();
    loginAsClient(clientEmail, clientPassword);
  }

  const labelStyle: CSSProperties = {
    display: 'block',
    marginBottom: 'var(--sp-1)',
    fontSize: 'var(--fs-sm)',
    fontWeight: 'var(--fw-medium)',
    color: 'var(--color-ink)',
  };
  const helpStyle: CSSProperties = {
    margin: '0 0 var(--sp-3)',
    fontSize: 'var(--fs-xs)',
    color: 'var(--color-ink-3)',
  };
  const divider = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', margin: 'var(--sp-6) 0 var(--sp-5)' }}>
      <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>or</span>
      <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
    </div>
  );
  const sectionTitleStyle: CSSProperties = {
    margin: '0 0 var(--sp-3)',
    fontSize: 'var(--fs-sm)',
    fontWeight: 'var(--fw-semibold)',
    color: 'var(--color-ink)',
    letterSpacing: 'var(--ls-tight)',
  };

  // The shared staff-password box. Hidden outright when the server says that login
  // cannot succeed; in demo mode collapsed behind a text link so the email form is the
  // obvious path. Rendered from ONE definition in both layouts.
  const sharedBox = !sharedPassword ? null : (demoMode && !showShared) ? (
    <section>
      <button
        type="button"
        onClick={() => setShowShared(true)}
        style={{
          appearance: 'none', background: 'none', border: 0, padding: 0, cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', textDecoration: 'underline',
        }}
      >
        Use a shared password instead
      </button>
    </section>
  ) : (
    <section>
      <h2 style={sectionTitleStyle}>{demoMode ? 'Shared staff password' : 'Staff / demo sign-in'}</h2>
      {demoMode && (
        <p style={helpStyle}>For instances that still use one shared staff login. Everyone with their own account signs in above.</p>
      )}
      <form onSubmit={handleSubmit}>
        <label style={{ display: 'block', marginBottom: 'var(--sp-4)' }}>
          <span style={labelStyle}>{demoMode ? 'Shared staff password' : 'Password'}</span>
          <Input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            invalid={!!error}
            aria-label={demoMode ? 'Shared staff password' : 'Staff password'}
            placeholder="Shared staff password"
          />
          {error && (
            <div style={{
              marginTop: 'var(--sp-1)',
              fontSize: 'var(--fs-sm)',
              color: 'var(--color-status-red)',
            }}>{error}</div>
          )}
        </label>
        <Button
          type="submit"
          variant="primary"
          size="md"
          loading={submitting}
          disabled={!password || submitting}
          style={{ width: '100%' }}
        >
          {demoMode ? 'Sign in with shared password' : 'Sign in'}
        </Button>
      </form>
    </section>
  );

  return (
    <div style={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      background: 'var(--color-bg)',
      padding: 'var(--sp-5)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-2xl)',
        boxShadow: 'var(--sh-lg)',
        padding: 'var(--sp-7)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 'var(--sp-6)' }}>
          <img
            src={brandLogo}
            alt="AppealStack"
            style={{ display: 'block', margin: '0 auto var(--sp-2)', width: 200, maxWidth: '78%', height: 'auto' }}
          />
          <h1 style={{
            position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
            overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
          }}>AppealStack</h1>
          <p style={{
            margin: 'var(--sp-1) 0 0',
            color: 'var(--color-ink-3)',
            fontSize: 'var(--fs-sm)',
          }}>Sign in to continue</p>
        </div>

        {/* In demo mode every account has its own password, so the email + password form is
            the sign-in for staff and clients alike and comes first. The shared-password box
            stays underneath for instances that still use one staff login. Outside demo mode
            the shared box is the staff sign-in and the portal link follows it. */}
        {demoMode ? (
          <>
        {/* ── Client portal sign-in ── */}
        {(demoMode || workosEnabled) && (
          <section style={demoMode ? undefined : { marginTop: 'var(--sp-6)' }}>
            {!demoMode && divider}
            <h2 style={sectionTitleStyle}>{demoMode ? 'Sign in' : 'Client portal sign-in'}</h2>
            {demoMode && <p style={helpStyle}>Use your email and password. Staff land on the dashboard, clients in their portal.</p>}

            {demoMode ? (
              <form onSubmit={handleClientSubmit}>
                <label style={{ display: 'block', marginBottom: 'var(--sp-4)' }}>
                  <span style={labelStyle}>Email</span>
                  <Input
                    type="email"
                    value={clientEmail}
                    onChange={e => setClientEmail(e.target.value)}
                    autoComplete="username"
                    invalid={!!clientError}
                    placeholder="you@example.com"
                  />
                </label>
                <label style={{ display: 'block', marginBottom: 'var(--sp-4)' }}>
                  <span style={labelStyle}>Password</span>
                  <Input
                    type="password"
                    value={clientPassword}
                    onChange={e => setClientPassword(e.target.value)}
                    autoComplete="current-password"
                    invalid={!!clientError}
                    aria-label="Password"
                    placeholder="Password"
                  />
                  {clientError && (
                    <div style={{
                      marginTop: 'var(--sp-1)',
                      fontSize: 'var(--fs-sm)',
                      color: 'var(--color-status-red)',
                    }}>{clientError}</div>
                  )}
                </label>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  loading={clientSubmitting}
                  disabled={!clientEmail || !clientPassword || clientSubmitting}
                  style={{ width: '100%' }}
                >
                  Sign in
                </Button>

                {/* Quick-pick — only when the server reports real client logins for THIS org.
                    An empty list renders nothing at all, rather than an empty dashed box
                    inviting a click that cannot work. */}
                {demoClients.length > 0 && (
                <div style={{
                  marginTop: 'var(--sp-4)',
                  padding: 'var(--sp-3)',
                  borderRadius: 'var(--r-lg)',
                  background: 'var(--color-bg)',
                  border: '1px dashed var(--color-border)',
                }}>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', marginBottom: 'var(--sp-2)' }}>
                    Client portal — click to fill the email
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                    {demoClients.map((c) => (
                      <button
                        key={c.email}
                        type="button"
                        disabled={clientSubmitting}
                        onClick={() => {
                          // Email only. The password is deliberately NOT filled: putting it
                          // here is what shipped a credential in the bundle.
                          setClientEmail(c.email);
                          setClientError(null);
                        }}
                        style={{
                          appearance: 'none',
                          cursor: 'pointer',
                          border: '1px solid var(--color-border)',
                          background: 'var(--color-card)',
                          color: 'var(--color-ink)',
                          borderRadius: 'var(--r-md)',
                          padding: 'var(--sp-1) var(--sp-3)',
                          fontSize: 'var(--fs-xs)',
                          fontFamily: 'inherit',
                        }}
                      >
                        {c.label}
                        <span style={{ color: 'var(--color-ink-3)' }}> · {c.email}</span>
                      </button>
                    ))}
                  </div>
                </div>
                )}
              </form>
            ) : (
              <>
                <a
                  href="/api/auth/workos/start"
                  style={{
                    display: 'block', width: '100%', boxSizing: 'border-box', textAlign: 'center',
                    padding: 'var(--sp-3)', borderRadius: 'var(--r-lg)',
                    border: '1px solid var(--color-border)', background: 'var(--color-card)',
                    color: 'var(--color-ink)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)',
                    textDecoration: 'none',
                  }}
                >
                  Client portal sign-in
                </a>
                <p style={{ margin: 'var(--sp-2) 0 0', textAlign: 'center', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                  Clients sign in here to view their properties, documents, and messages.
                </p>
              </>
            )}
          </section>
        )}

        {sharedBox && <>{divider}{sharedBox}</>}

          </>
        ) : (
          <>
        {sharedBox}

        {/* ── Client portal sign-in ── */}
        {(demoMode || workosEnabled) && (
          <section style={demoMode ? undefined : { marginTop: 'var(--sp-6)' }}>
            {!demoMode && divider}
            <h2 style={sectionTitleStyle}>{demoMode ? 'Sign in' : 'Client portal sign-in'}</h2>
            {demoMode && <p style={helpStyle}>Use your email and password. Staff land on the dashboard, clients in their portal.</p>}

            {demoMode ? (
              <form onSubmit={handleClientSubmit}>
                <label style={{ display: 'block', marginBottom: 'var(--sp-4)' }}>
                  <span style={labelStyle}>Email</span>
                  <Input
                    type="email"
                    value={clientEmail}
                    onChange={e => setClientEmail(e.target.value)}
                    autoComplete="username"
                    invalid={!!clientError}
                    placeholder="you@example.com"
                  />
                </label>
                <label style={{ display: 'block', marginBottom: 'var(--sp-4)' }}>
                  <span style={labelStyle}>Password</span>
                  <Input
                    type="password"
                    value={clientPassword}
                    onChange={e => setClientPassword(e.target.value)}
                    autoComplete="current-password"
                    invalid={!!clientError}
                    aria-label="Password"
                    placeholder="Password"
                  />
                  {clientError && (
                    <div style={{
                      marginTop: 'var(--sp-1)',
                      fontSize: 'var(--fs-sm)',
                      color: 'var(--color-status-red)',
                    }}>{clientError}</div>
                  )}
                </label>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  loading={clientSubmitting}
                  disabled={!clientEmail || !clientPassword || clientSubmitting}
                  style={{ width: '100%' }}
                >
                  Sign in
                </Button>

                {/* Quick-pick — only when the server reports real client logins for THIS org.
                    An empty list renders nothing at all, rather than an empty dashed box
                    inviting a click that cannot work. */}
                {demoClients.length > 0 && (
                <div style={{
                  marginTop: 'var(--sp-4)',
                  padding: 'var(--sp-3)',
                  borderRadius: 'var(--r-lg)',
                  background: 'var(--color-bg)',
                  border: '1px dashed var(--color-border)',
                }}>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', marginBottom: 'var(--sp-2)' }}>
                    Client portal — click to fill the email
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                    {demoClients.map((c) => (
                      <button
                        key={c.email}
                        type="button"
                        disabled={clientSubmitting}
                        onClick={() => {
                          // Email only. The password is deliberately NOT filled: putting it
                          // here is what shipped a credential in the bundle.
                          setClientEmail(c.email);
                          setClientError(null);
                        }}
                        style={{
                          appearance: 'none',
                          cursor: 'pointer',
                          border: '1px solid var(--color-border)',
                          background: 'var(--color-card)',
                          color: 'var(--color-ink)',
                          borderRadius: 'var(--r-md)',
                          padding: 'var(--sp-1) var(--sp-3)',
                          fontSize: 'var(--fs-xs)',
                          fontFamily: 'inherit',
                        }}
                      >
                        {c.label}
                        <span style={{ color: 'var(--color-ink-3)' }}> · {c.email}</span>
                      </button>
                    ))}
                  </div>
                </div>
                )}
              </form>
            ) : (
              <>
                <a
                  href="/api/auth/workos/start"
                  style={{
                    display: 'block', width: '100%', boxSizing: 'border-box', textAlign: 'center',
                    padding: 'var(--sp-3)', borderRadius: 'var(--r-lg)',
                    border: '1px solid var(--color-border)', background: 'var(--color-card)',
                    color: 'var(--color-ink)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)',
                    textDecoration: 'none',
                  }}
                >
                  Client portal sign-in
                </a>
                <p style={{ margin: 'var(--sp-2) 0 0', textAlign: 'center', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                  Clients sign in here to view their properties, documents, and messages.
                </p>
              </>
            )}
          </section>
        )}

          </>
        )}

        <footer style={{
          marginTop: 'var(--sp-6)',
          paddingTop: 'var(--sp-4)',
          borderTop: '1px solid var(--color-border)',
          textAlign: 'center',
        }}>
          <a href="/v1/login.html" style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)', textDecoration: 'none' }}>
            Use v1 login
          </a>
          {/* Legal notices, reachable before anyone signs in — which is the only
              point at which a privacy notice is actually useful. */}
          <LegalFooterLinks style={{ marginTop: 'var(--sp-3)' }} />
        </footer>
      </div>
    </div>
  );
}
